import React, { useRef } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataList,
  DownloadButton,
  EmptyContent,
  Typography,
  VisuallyHidden,
} from '@/components/ui';
import {
  IconAlertTriangleFilled,
  IconCloudUpload,
  IconUpload,
} from '@tabler/icons-react';
import { WhitelistUploadCardProps } from './types';

// Generate sample CSV content for template download
const generateSampleCSV = (): string => {
  const headers = ['filename', 'category', 'description', 'priority'];
  const sampleData = [
    ['document.pdf', 'documents', 'Important business document', 'high'],
    ['presentation.pptx', 'presentations', 'Q1 sales presentation', 'medium'],
    ['data_export.xlsx', 'spreadsheets', 'Customer data export', 'high'],
    ['logo.png', 'images', 'Company logo file', 'low'],
    ['video_tutorial.mp4', 'videos', 'Training material', 'medium'],
    ['report_2024.docx', 'documents', 'Annual report document', 'high'],
    ['budget.xlsx', 'spreadsheets', 'Budget planning sheet', 'high'],
    ['team_photo.jpg', 'images', 'Team building photo', 'low'],
    ['meeting_recording.mp3', 'audio', 'Important meeting recording', 'medium'],
    ['backup_data.zip', 'archives', 'System backup file', 'high'],
    ['contract.pdf', 'documents', 'Legal contract document', 'high'],
    ['marketing_video.mov', 'videos', 'Marketing campaign video', 'medium'],
    ['financial_data.csv', 'data', 'Financial analysis data', 'high'],
    ['user_manual.pdf', 'documents', 'Product user manual', 'medium'],
    ['design_mockup.psd', 'designs', 'UI design mockup', 'low'],
  ];

  const csvContent = [
    headers.join(','),
    ...sampleData.map(row => row.map(cell => `"${cell}"`).join(',')),
  ].join('\n');

  return csvContent;
};

const getTemplateBlobUrl = (): string => {
  const csvContent = generateSampleCSV();
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  return URL.createObjectURL(blob);
};

export const WhitelistUploadCard: React.FC<WhitelistUploadCardProps> = ({
  onFileUpload,
  isUploading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileUpload(file);
      // Clear the input so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card disablePadding className="whitelist-upload-card">
      <CardHeader
        cardTitle="Upload New Whitelist"
        description="File must contain a &ldquo;filename&rdquo; column"
      />

      <CardContent className="whitelist-upload-card__content">
        <div className="whitelist-upload-card__dropzone" onClick={handleClick}>
          <VisuallyHidden>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="whitelist-upload-card__hidden-input"
              disabled={isUploading}
            />
          </VisuallyHidden>

          <div className="whitelist-upload-card__dropzone-content">
            {isUploading ? (
              <div className="whitelist-upload-card__uploading">
                <IconCloudUpload
                  size={32}
                  className="whitelist-upload-card__uploading-icon"
                />
                <Typography variant="body1">Uploading...</Typography>
              </div>
            ) : (
              <>
                <Button
                  mb="4"
                  fullWidth
                  startIcon={<IconUpload />}
                  size="small"
                  color="primary"
                  variant="classic"
                  disabled={isUploading}
                  className="whitelist-upload-card__upload-btn"
                >
                  Upload new whitelist
                </Button>
                <VisuallyHidden>
                  <EmptyContent
                    size="small"
                    icon={<IconCloudUpload />}
                    title="Click to select file"
                    description="CSV up to 50MB"
                  />
                </VisuallyHidden>
              </>
            )}
          </div>
        </div>
        <Alert
          size="small"
          title="File Requirements:"
          description={
            <>
              <DataList.Root size="small" orientation="horizontal" mt="2">
                <DataList.Item>
                  <DataList.Label>Required column</DataList.Label>
                  <DataList.Value>
                    {' '}
                    &ldquo;filename&rdquo; (case-sensitive)
                  </DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label>Supported formats:</DataList.Label>
                  <DataList.Value>CSV</DataList.Value>
                </DataList.Item>
                <DataList.Item>
                  <DataList.Label>Maximum size:</DataList.Label>
                  <DataList.Value>50MB</DataList.Value>
                </DataList.Item>
              </DataList.Root>
            </>
          }
        >
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Alert
              size="xsmall"
              variant="soft"
              color="info"
              icon={<IconAlertTriangleFilled />}
              title="New file automatically becomes active"
            />
          </div>
        </Alert>

        <Alert
          size="medium"
          title="Need a template?"
          description={'Download our sample file to see the correct format'}
        >
          <div style={{ marginTop: 'var(--space-2)' }}>
            <DownloadButton
              fileUrl={getTemplateBlobUrl()}
              filename="whitelist_template.csv"
              variant="outlined"
              size="small"
              className="whitelist-upload-card__template-btn"
              successMessage="Template file downloaded successfully"
              errorMessage="Failed to download template"
            >
              Download Template
            </DownloadButton>
          </div>
        </Alert>
      </CardContent>
    </Card>
  );
};
